import { useId, useState } from "react";
import Reveal from "../common/Reveal";
import { API_BASE as API } from "../../api/api";
import { NEWSLETTER_COPY as COPY, NEWSLETTER_FLAGS as FLAGS } from "../../data/newsletterConfig";
import "./Newsletter.css";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const Newsletter = () => {
  const emailId    = useId();
  const whatsappId = useId();
  const honeyId    = useId();

  const [email, setEmail]         = useState("");
  const [wantsWhatsapp, setWants] = useState(false);
  const [whatsapp, setWhatsapp]   = useState("");
  const [honeypot, setHoneypot]   = useState(""); // hidden — real users never touch this
  const [status, setStatus]       = useState("idle"); // idle | submitting | success | error
  const [error, setError]         = useState("");

  const subtext = FLAGS.market === "KE" ? COPY.subtextKE : COPY.subtext;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!EMAIL_RE.test(email)) {
      setStatus("error");
      setError("Please enter a valid email address.");
      return;
    }
    if (wantsWhatsapp && !whatsapp.trim()) {
      setStatus("error");
      setError("Add your WhatsApp number, or untick the WhatsApp option.");
      return;
    }

    setStatus("submitting");
    setError("");
    try {
      const r = await fetch(`${API}/newsletter/subscribe`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email,
          whatsapp_number: wantsWhatsapp ? whatsapp.trim() : null,
          channel: wantsWhatsapp ? "both" : "email",
          source: "newsletter_footer",
          website: honeypot,
        }),
      });
      if (!r.ok) {
        const body = await r.json().catch(() => ({}));
        throw new Error(body.detail || "Something went wrong — please try again.");
      }
      setStatus("success");
    } catch (err) {
      setStatus("error");
      setError(err.message || "Something went wrong — please try again.");
    }
  };

  return (
    <Reveal as="section" className="newsletter reveal">
      <div className="newsletter-inner">
        <h2 className="nl-script">{COPY.eyebrow}</h2>
        <p>{subtext}</p>

        {status === "success" ? (
          <p className="nl-success" role="status">{COPY.successMessage}</p>
        ) : (
          <form className="nl-form-wrap" onSubmit={handleSubmit} noValidate>
            <div className="nl-form">
              <label className="nl-visually-hidden" htmlFor={emailId}>Email address</label>
              <input
                id={emailId}
                type="email"
                required
                autoComplete="email"
                placeholder={COPY.emailPlaceholder}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                aria-invalid={status === "error"}
                aria-describedby={status === "error" ? `${emailId}-error` : undefined}
              />
              {/* Honeypot — visually hidden (not display:none, so bots that
                  skip display:none-hidden fields still fall for it), real
                  users never see or fill it. */}
              <label className="nl-honeypot" htmlFor={honeyId} aria-hidden="true">
                Leave this field blank
                <input
                  id={honeyId}
                  type="text"
                  tabIndex={-1}
                  autoComplete="off"
                  value={honeypot}
                  onChange={(e) => setHoneypot(e.target.value)}
                />
              </label>
              <button type="submit" disabled={status === "submitting"}>
                {status === "submitting" ? "Subscribing…" : COPY.subscribeLabel}
              </button>
            </div>

            {FLAGS.enableWhatsApp && (
              <div className="nl-whatsapp">
                <label className="nl-checkbox">
                  <input
                    type="checkbox"
                    checked={wantsWhatsapp}
                    onChange={(e) => setWants(e.target.checked)}
                  />
                  {COPY.whatsappToggleLabel}
                </label>
                {wantsWhatsapp && (
                  <>
                    <label className="nl-visually-hidden" htmlFor={whatsappId}>WhatsApp number</label>
                    <input
                      id={whatsappId}
                      className="nl-whatsapp-input"
                      type="tel"
                      autoComplete="tel"
                      placeholder={COPY.whatsappPlaceholder}
                      value={whatsapp}
                      onChange={(e) => setWhatsapp(e.target.value)}
                    />
                  </>
                )}
              </div>
            )}

            {status === "error" && (
              <p className="nl-error" id={`${emailId}-error`} role="alert">{error}</p>
            )}
          </form>
        )}
      </div>
    </Reveal>
  );
};

export default Newsletter;
